/**
 * Prospects API endpoints
 *
 * Card 6a333d48 — [CHAT][AUTH] Merge-on-login session_id → user_id (F2).
 *
 * Wrapper para el endpoint user-facing ``POST /api/v1/prospects/claim``.
 * Llamado por ``useProspectClaim`` cuando el usuario se loguea y tenemos
 * un ``conversationId`` persistido en localStorage (PR #409 / card 6a333ce6).
 *
 * El endpoint es idempotente:
 * - 200 + ``outcome="claimed"``: primer merge exitoso.
 * - 200 + ``outcome="already_owned"``: idempotente (segundo tab, retry).
 * - 404: no hay prospect para esa session. Tratamos como no-op silencioso.
 * - 409: otro user ya reclamó esa session. Log silencioso para no romper UX.
 */

import { apiClient } from '../client';

/**
 * Outcomes posibles del merge.
 *
 * ``claimed`` / ``already_owned`` corresponden a 200; ``conflict`` se
 * propaga como excepción del axios client (HTTP 409) y se trata en el caller
 * (no llega a esta variable).
 */
export type ProspectClaimOutcome = 'claimed' | 'already_owned';

export interface ProspectClaimResponse {
  id: string;
  session_id: string;
  user_id: number | null;
  outcome: ProspectClaimOutcome;
  // Campos extra del ProspectResponse — los exponemos por completitud
  // aunque el hook actual no los consume.
  wizard_profile?: string | null;
  wizard_score_vis?: number | null;
  wizard_score_no_vis?: number | null;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

/**
 * Reclama un prospect anónimo para el usuario autenticado.
 *
 * El backend resuelve el ``user_id`` desde el JWT (``Depends(get_current_user)``).
 * Esta función NO maneja autenticación manualmente — el ``apiClient``
 * inyecta el ``Authorization: Bearer <token>`` vía interceptor.
 *
 * Errores que propaga (axios throws):
 * - 401 sin token válido (apiClient interceptor redirige a Auth0).
 * - 404 cuando no hay prospect (el hook lo silencia, es un edge benigno).
 * - 409 cuando otro user ya reclamó la session (el hook lo loguea).
 *
 * @param sessionId - ``conversationId`` persistido del anónimo.
 */
export async function claimProspect(
  sessionId: string,
): Promise<ProspectClaimResponse> {
  const response = await apiClient.post<ProspectClaimResponse>(
    '/api/v1/prospects/claim',
    { session_id: sessionId },
  );
  return response.data;
}
